import Button from "@/common/components/Button";
import MaskedTextField from "@/common/components/MaskedTextField";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import TextField from "@/common/components/TextField";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { SettingsIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useUpdateTefProductCode } from "../../hooks/useUpdateTefProductCode";
import { tefProductCodeUpdateSchema, TefProductCodeUpdateValidationSchema } from "../../schemas/tefProductCode.schema";
import { tefProviders } from "../../constants/tefModels.constants";
import { TefProductCodeData } from "../../types/tefProductCodes.type";
import { numericRegexMask } from "@/common/constants/masks.constant";

interface TefConfigurationProductUpdateModalProps {
  tefProductCode: TefProductCodeData;
}

const TefConfigurationProductUpdateModal = ({ tefProductCode }: TefConfigurationProductUpdateModalProps) => {
  const { provider } = useParams();

  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    mutate: updateTefProductCode,
    isPending: updateTefProductCodeIsPending,
    isError: updateTefProductCodeIsError,
  } = useUpdateTefProductCode({
    id: tefProductCode.id,
    productId: tefProductCode.product?.id,
    provider: provider,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tefProductCodes'] });
      setMessages(['Produto ajustado com sucesso!']);
      setIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  })

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors }
  } = useForm<TefProductCodeUpdateValidationSchema>({
    resolver: zodResolver(tefProductCodeUpdateSchema),
    defaultValues: {
      code: tefProductCode.code,
    }
  });

  const onSubmit: SubmitHandler<TefProductCodeUpdateValidationSchema> = (data) => updateTefProductCode(data);

  useEffect(() => reset({ code: tefProductCode.code }), [isOpen, tefProductCode.code]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            variant="link-white"
            name="update_tef_product_code"
            operationKlassToCheckPermission="Establishment::TefProductCodes::Update"
          >
            <SettingsIcon strokeWidth={1.5} />
          </Button>
        )}
        dataTestid="submit_update_tef_product_code"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={updateTefProductCodeIsPending}
        disabled={updateTefProductCodeIsPending}
        footer="Confirmar ajuste e salvar alteração"
        title="Ajuste de código"
        body={(
          <div className="flex flex-col gap-y-4 text-white-800">
            <TextField
              value={tefProductCode.product?.display_name}
              label="Produto"
              disabled
            />

            <Controller
              control={control}
              name="code"
              render={({ field }) => (
                <MaskedTextField
                  {...field}
                  label={`Código ${provider ? tefProviders[provider]?.label : ''}`}
                  mask={provider ? tefProviders[provider].regexMaskFn(tefProviders[provider].codeLength) : numericRegexMask(6)}
                  prepareChar={(char) => char.toUpperCase()}
                  markAsRequired
                  errorMessage={errors.code?.message || undefined}
                />
              )}
            />
          </div>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={updateTefProductCodeIsError}
        />
      )}
    </>
  )
}

export default TefConfigurationProductUpdateModal;
